"use client";

import { Suspense } from "react";
import type { CatalogContentBlock } from "@/domain/catalog";
import { pickBlockComponent } from "./helpers/content-blocks-map";
import { RecipePairings } from "./blocks/RecipePairings";
import type { NormalizedRecipe } from "./helpers/recipe-pairings/normalized-recipe";
import * as styles from "./ContentBlocksRenderer.module.scss";

export interface ContentBlocksRendererProps {
	blocks: CatalogContentBlock[];
	parentSku?: string;
	/**
	 * Ordinal-indexed recipes resolved by the route via the generic CMS
	 * retrieve. Nth entry → Nth `recipe_pairings_block`. Renderer special-
	 * cases that type so `RecipePairingsBlock` stays a pure wire shape.
	 */
	resolvedRecipePairings?: NormalizedRecipe[][];
}

export function ContentBlocksRenderer({
	blocks,
	parentSku,
	resolvedRecipePairings
}: ContentBlocksRendererProps) {
	if (!blocks?.length) return null;

	let recipePairingsOrdinal = 0;

	return (
		<div
			className={styles["content-blocks-renderer"]}
			data-component="ContentBlocksRenderer"
		>
			{blocks.map((block, i) => {
				if (block.blockType === "recipe_pairings_block") {
					const recipes =
						resolvedRecipePairings?.[recipePairingsOrdinal++] ?? [];
					return (
						<BlockBoundary
							key={`${block.blockType}-${i}`}
							type={block.blockType}
						>
							<Suspense
								fallback={
									<BlockSkeleton type={block.blockType} />
								}
							>
								<RecipePairings
									block={block}
									recipes={recipes}
								/>
							</Suspense>
						</BlockBoundary>
					);
				}
				const Block = pickBlockComponent(block);
				return (
					<BlockBoundary
						key={`${block.blockType}-${i}`}
						type={block.blockType}
					>
						<Suspense
							fallback={<BlockSkeleton type={block.blockType} />}
						>
							<Block block={block} parentSku={parentSku} />
						</Suspense>
					</BlockBoundary>
				);
			})}
		</div>
	);
}

function BlockBoundary({
	type,
	children
}: {
	type: string;
	children: React.ReactNode;
}) {
	return (
		<div
			data-block-boundary={type}
			className={styles["content-blocks-renderer__item"]}
		>
			{children}
		</div>
	);
}

function BlockSkeleton({ type }: { type: string }) {
	return (
		<div
			role="status"
			aria-label={`Loading ${type}`}
			data-skeleton={type}
			className={styles["content-blocks-renderer__skeleton"]}
		/>
	);
}
